import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  fetchEsriImageryInfo,
  fetchNationalOrthoYear,
  formatImageryDate,
  type ImageryInfo,
} from './imagery';
import { useNationalOrthoLayers } from './useNationalOrthoLayers';

export type ImageryLayerKey = 'satellite' | 'ortho';

const DEBOUNCE_MS = 400;

/**
 * Capture date of the imagery under the map centre, for the photo base
 * layers. Rendered into a real Leaflet control (bottom-right, stacked above
 * the attribution) so it never fights the corner containers.
 */
export function ImageryDateReadout({ layer }: { layer: ImageryLayerKey }) {
  const { t } = useTranslation(['common']);
  const map = useMap();
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [info, setInfo] = useState<ImageryInfo | null>(null);
  const orthoLayers = useNationalOrthoLayers();

  useEffect(() => {
    const control = new L.Control({ position: 'bottomright' });
    control.onAdd = () => {
      const el = L.DomUtil.create('div', 'leaflet-control !m-0 !mb-1 !mr-1');
      L.DomEvent.disableClickPropagation(el);
      return el;
    };
    control.addTo(map);
    setHost(control.getContainer() ?? null);
    return () => {
      control.remove();
      setHost(null);
    };
  }, [map]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ctrl: AbortController | undefined;

    const lookup = async (signal: AbortSignal): Promise<ImageryInfo> => {
      const c = map.getCenter();
      const zoom = map.getZoom();
      if (layer === 'ortho') {
        const year = await fetchNationalOrthoYear(c.lat, c.lng, orthoLayers, signal);
        if (year) return { kind: 'dated', source: 'ortho', date: String(year) };
        // Outside every survey the layer shows Esri underneath, so date that.
      }
      return fetchEsriImageryInfo(c.lat, c.lng, zoom, signal);
    };

    const refresh = () => {
      clearTimeout(timer);
      ctrl?.abort();
      setInfo(null);
      timer = setTimeout(() => {
        ctrl = new AbortController();
        const signal = ctrl.signal;
        lookup(signal)
          .then((next) => {
            if (!signal.aborted) setInfo(next);
          })
          .catch(() => {
            if (!signal.aborted) setInfo({ kind: 'unknown' });
          });
      }, DEBOUNCE_MS);
    };

    refresh();
    map.on('moveend', refresh);
    return () => {
      map.off('moveend', refresh);
      clearTimeout(timer);
      ctrl?.abort();
    };
  }, [map, layer, orthoLayers]);

  if (!host) return null;

  let text: string;
  if (!info) text = t('common:map.imagery.loading');
  else if (info.kind === 'zoomIn') text = t('common:map.imagery.zoomIn');
  else if (info.kind === 'unknown') text = t('common:map.imagery.unknown');
  else {
    const parts = [
      `${t('common:map.imagery.taken')}: ${formatImageryDate(info.date)}`,
      t(`common:map.imagery.source.${info.source}`),
      info.provider,
      info.resolutionM !== undefined ? `${info.resolutionM} m` : undefined,
    ];
    text = parts.filter(Boolean).join(' · ');
  }

  return createPortal(
    <div
      className="rounded border border-slate-300 bg-white/90 px-1.5 py-0.5 text-[11px] text-slate-700 shadow-sm backdrop-blur-sm dark:border-slate-600 dark:bg-slate-800/90 dark:text-slate-200"
      title={t('common:map.imagery.hint')}
      aria-live="polite"
    >
      {text}
    </div>,
    host,
  );
}
